import { Fragment } from "react";

import {
  homepageImageUrl,
  type HomepageSections,
} from "@/data/homepageContent";
import { usePartnerLogos } from "@/data/partnerLogos";
import { useVorinAssets } from "@/pages/shared/useVorinAssets";
import { MINIMALISTIC_PORTFOLIO_STYLESHEETS } from "@/pages/shared/vorinDemoStyles";

import styles from "./PostHomepageSections.module.scss";

type PostHomepageSectionsProps = {
  content: HomepageSections;
};

export function PostHomepageSections({ content }: PostHomepageSectionsProps) {
  useVorinAssets({ extraStylesheets: MINIMALISTIC_PORTFOLIO_STYLESHEETS });
  const partnerLogos = usePartnerLogos();
  const { work, capabilities, purpose, closing } = content;
  const showcaseColumns = ["1", "2", "3"].map((column) =>
    work.showcase_items.filter((item) => String(item.column) === column),
  );

  return (
    <div className={styles.stack}>
      <section
        className={`pt-40px pb-40px md-pt-30px md-pb-30px border-bottom border-top border-color-medium-gray ${styles.marqueeBand}`}
        data-chrome-surface="dark"
      >
        <div className="container-fluid">
          <div className="row position-relative">
            <div className="col swiper swiper-width-auto text-center">
              <div className={styles.caseMarquee}>
                <div className={styles.caseMarqueeTrack}>
                  {[...work.marquee_words, ...work.marquee_words].map((word, index) => (
                    <span key={`${word}-${index}`} className={styles.caseMarqueeWord}>
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className={`ps-65px pe-65px sm-pe-0 sm-ps-0 lg-pe-15px lg-ps-15px xxl-pe-45px xxl-ps-45px pt-0 pb-0 position-relative overflow-hidden ${styles.darkSurfaceSection}`}
        data-chrome-surface="dark"
      >
        <div className="container-fluid">
          <div className={`row ${styles.caseIntroRow}`}>
            <div className={`col-12 ${styles.caseIntroCentered}`}>
              <h2 className={styles.heroStatement}>{work.title}</h2>
            </div>
          </div>

          <a href={work.lead_href} className={styles.venueLead}>
            <img
              src={homepageImageUrl(work.lead_image)}
              alt={work.lead_image.alt}
            />
            <span>{work.lead_caption}</span>
          </a>

          <div className={`row ${styles.caseColumnsRow}`}>
            {showcaseColumns.map((column, columnIndex) => (
              <div
                key={`column-${columnIndex}`}
                className={`col-md-4 col-lg-4 ${styles.caseColumn}`}
              >
                <div className={styles.caseColumnStack}>
                  {column.map((item) => (
                    <article key={item.title} className={styles.caseItem}>
                      <div className="portfolio-box mousetip-wrapper">
                        <a href={item.href} className={styles.caseAnchor}>
                          <div className={`overflow-hidden ${styles.caseImageFrame}`}>
                            <img src={homepageImageUrl(item.image)} alt={item.image.alt || item.title} />
                          </div>
                          <div className={`bg-dark-gray ${styles.caseCaption}`}>
                            <span className={styles.caseMeta}>{item.meta}</span>
                            <span className={`fs-20 fw-500 ${styles.caseTitle}`}>{item.title}</span>
                          </div>
                        </a>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {partnerLogos.length > 0 && (
        <section
          className={styles.partnerBand}
          data-chrome-surface="dark"
          aria-label="VenueX partners"
        >
          <div className={styles.partnerViewport}>
            <div className={styles.partnerTrack}>
              {[...partnerLogos, ...partnerLogos].map((partner, index) => {
                const isDuplicate = index >= partnerLogos.length;
                const content = partner.logoUrl ? (
                  <img src={partner.logoUrl} alt={isDuplicate ? "" : partner.name} />
                ) : (
                  <span className={styles.partnerWordmark}>{partner.name}</span>
                );

                return partner.websiteUrl ? (
                  <a
                    key={`${partner.id}-${index}`}
                    className={styles.partnerMark}
                    href={partner.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-hidden={isDuplicate || undefined}
                    tabIndex={isDuplicate ? -1 : 0}
                    aria-label={isDuplicate ? undefined : partner.name}
                  >
                    {content}
                  </a>
                ) : (
                  <div
                    key={`${partner.id}-${index}`}
                    className={styles.partnerMark}
                    aria-hidden={isDuplicate || undefined}
                    aria-label={isDuplicate ? undefined : partner.name}
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section
        className={`ps-65px pe-65px sm-pe-0 sm-ps-0 lg-pe-15px lg-ps-15px xxl-pe-45px xxl-ps-45px pt-0 position-relative ${styles.darkSurfaceSection}`}
        data-chrome-surface="dark"
      >
        <div className="container-fluid">
          <div className={styles.systemShowcaseSection}>
            <div className={styles.systemShowcaseTopline}>
              <span className={styles.sectionLeadKicker}>&#10022; {capabilities.kicker}</span>
              <a href={capabilities.link.href} className={styles.templateLink}>
                {capabilities.link.label}
              </a>
            </div>

            <div className={styles.systemShowcaseHero}>
              <p className={styles.systemShowcaseIntro}>
                {capabilities.intro}
              </p>
              <h3 className={styles.systemShowcaseTitle}>
                {capabilities.title_lines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </h3>
            </div>

            <div className={styles.systemShowcaseCards}>
              {capabilities.cards.map((card, index) => (
                <article key={card.title} className={styles.systemShowcaseCard}>
                  <a href={card.href} className={styles.systemShowcaseCardLink}>
                    <div className={styles.systemShowcaseImageWrap}>
                      <img
                        src={homepageImageUrl(card.image)}
                        alt={card.image.alt || card.title}
                        className={styles.systemShowcaseImage}
                      />
                    </div>
                    <div className={styles.systemShowcaseCardCopy}>
                      <span>{`0${index + 1} / ${card.meta}`}</span>
                      <h4>{card.title}</h4>
                      <p>{card.note}</p>
                    </div>
                  </a>
                </article>
              ))}
            </div>

            <div className={styles.systemShowcaseLabels}>
              {capabilities.labels.map((label) => (
                <span key={label} className={styles.systemShowcaseLabel}>
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className={`ps-65px pe-65px sm-pe-0 sm-ps-0 lg-pe-15px lg-ps-15px xxl-pe-45px xxl-ps-45px pt-0 ${styles.darkSurfaceSection}`}
        data-chrome-surface="dark"
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-8 col-xl-7 offset-lg-1">
              <span className={styles.sectionEyebrow}>{purpose.eyebrow}</span>
              <h2 className={styles.heroStatement}>{purpose.title}</h2>
            </div>
          </div>

          <div className={styles.reasonRows}>
            {purpose.reasons.map((reason) => (
              <article key={reason.index} className={styles.reasonRow}>
                <span className={styles.reasonNumber}>{reason.index}</span>
                <h3>{reason.title}</h3>
                <p>{reason.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className="ps-65px pe-65px sm-pe-0 sm-ps-0 lg-pe-15px lg-ps-15px xxl-pe-45px xxl-ps-45px pb-0 position-relative overflow-hidden"
        data-chrome-surface="dark"
      >
        <div className="container-fluid">
          <div className={styles.memoryHero}>
            <div className={styles.memoryCopy}>
              <span className={styles.memoryKicker}>{closing.kicker}</span>
              <h2>{closing.title}</h2>
              <p>{closing.body}</p>
              <div className={styles.memoryLinks}>
                <a href={closing.primary_link.href}>{closing.primary_link.label}</a>
                <a href={closing.secondary_link.href}>{closing.secondary_link.label}</a>
              </div>
              <div className={styles.memorySequence} aria-label="Identity to impact">
                {closing.sequence.map((item, index) => (
                  <Fragment key={`${item}-${index}`}>
                    {index > 0 && <i aria-hidden="true">&#8594;</i>}
                    <span>{item}</span>
                  </Fragment>
                ))}
              </div>
            </div>

            <div className={styles.memoryStrip}>
              {closing.frames.map((frame) => (
                <figure key={frame.title} className={styles.memoryFigure}>
                  <img src={homepageImageUrl(frame.image)} alt={frame.image.alt || frame.title} />
                  <figcaption>{frame.title}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
